import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import { getAuth, onAuthStateChanged, signOut as firebaseSignOut } from 'firebase/auth';
import type { User as FirebaseUser } from 'firebase/auth';
import type { FirebaseApp } from 'firebase/app';
import { exchangeFirebaseToken, refreshHetivoToken } from './token-exchange';
import type { TokenExchangeConfig } from './token-exchange';
import { useImpersonationSession } from './use-impersonation-session';
import { SESSION_ENDED_REASON } from './session-ended-reason';
import type { SessionEndedReason } from './session-ended-reason';

export { SESSION_ENDED_REASON } from './session-ended-reason';
export type { SessionEndedReason } from './session-ended-reason';

// --- Constants ---

const PROACTIVE_REFRESH_THRESHOLD_MS = 5 * 60 * 1000;

function isTokenExpiringSoon(expiryMs: number | null): boolean {
  if (!expiryMs) return false;
  return expiryMs - Date.now() < PROACTIVE_REFRESH_THRESHOLD_MS;
}

// --- Types ---

export interface HetivoAuthUser {
  email: string;
  name: string;
}

export interface SignInStrategies {
  /** Email/password sign-in */
  email?: (email: string, password: string) => Promise<void>;
  /** Google SSO popup */
  google?: () => Promise<void>;
  /** Microsoft SSO popup */
  microsoft?: () => Promise<void>;
  /** Send password reset email */
  resetPassword?: (email: string) => Promise<void>;
}

export interface HetivoAuthState {
  /** Auth error message (for display on login page) */
  error: string | null;
  /** Whether the user is authenticated (has valid Hetivo token) */
  isAuthenticated: boolean;
  /** Whether the current session is an impersonation session */
  isImpersonating: boolean;
  /** Whether auth state is still being determined */
  isLoading: boolean;
  /** The Hetivo JWT token (null if not authenticated) */
  hetivoToken: string | null;
  /** Reason the session status should be shown (null if there is no session status message to show) */
  sessionEndedReason: SessionEndedReason | null;
  /** Basic user info from Firebase */
  user: HetivoAuthUser | null;
}

export interface HetivoAuthContextValue extends HetivoAuthState {
  /** Get the current Hetivo token (for use as TokenProvider) */
  getToken: () => Promise<string | null>;
  /** Notify the auth provider that user activity occurred (e.g., an API request was sent).
   *  Resets the inactivity timer. Only meaningful when inactivityTimeoutMs is configured. */
  notifyActivity: () => void;
  /** Pause token exchange (used during activation flow) */
  pauseExchange: () => void;
  /** Resume token exchange */
  resumeExchange: () => void;
  /** Send password reset email (only available if resetPassword strategy was provided) */
  sendPasswordResetEmail?: (email: string) => Promise<void>;
  /** Sign in with email/password (only available if email strategy was provided) */
  signInWithEmail?: (email: string, password: string) => Promise<void>;
  /** Sign in with Google SSO (only available if google strategy was provided) */
  signInWithGoogle?: () => Promise<void>;
  /** Sign in with Microsoft SSO (only available if microsoft strategy was provided) */
  signInWithMicrosoft?: () => Promise<void>;
  /** Sign out and clear tokens */
  signOut: () => Promise<void>;
}

export interface HetivoAuthProviderProps {
  children: ReactNode;
  /** Callback to clear the persisted refresh token (on sign-out).
   *  Required when enableRefresh is true. */
  clearPersistedRefreshToken?: () => Promise<void>;
  /** Enable token refresh. When true, the provider will attempt to refresh
   *  expired tokens using the refresh token before signing out.
   *  Default: false (web behavior — sign out on expiry per PHA-46). */
  enableRefresh?: boolean;
  /** Config for token exchange */
  exchangeConfig: TokenExchangeConfig;
  /** Firebase app instance (from initializeFirebase) */
  firebaseApp: FirebaseApp;
  /** Callback to read the persisted refresh token.
   *  Required when enableRefresh is true. */
  getPersistedRefreshToken?: () => Promise<string | null>;
  /** Inactivity timeout in milliseconds. If set, auto-signs out after this period of no backend API requests. Callers must invoke notifyActivity() to reset the timer. */
  inactivityTimeoutMs?: number;
  /** Callback to persist the refresh token (e.g., expo-secure-store on mobile).
   *  Required when enableRefresh is true. */
  onRefreshTokenReceived?: (refreshToken: string) => Promise<void>;
  /** Optional callback invoked whenever a session ends, whatever the cause: explicit sign-out, idle
   *  timeout, 12h session timer, impersonation expiry, or any Firebase signed-out transition. Use this
   *  to clear app-level caches (e.g., React Query cache) so the next user does not see stale data.
   *  May be invoked more than once for a single sign-out (explicit sign-out, then the Firebase
   *  signed-out transition), so it must be idempotent. */
  onSignOut?: () => void;
  /** Optional synchronous function to read an impersonation token (e.g., from URL params).
   *  Called once on initial render. Return null if no impersonation token is present. */
  readImpersonationToken?: () => string | null;
  /** Session timeout in milliseconds (default: 12 * 60 * 60 * 1000 = 12 hours) */
  sessionTimeoutMs?: number;
  /** Sign-in strategies (app-specific). Each injected strategy gets wrapped with centralized loading/error handling. */
  signInStrategies?: SignInStrategies;
}

// --- Context ---

const HetivoAuthContext = createContext<HetivoAuthContextValue | null>(null);

// --- Provider ---

export function HetivoAuthProvider({
  children,
  clearPersistedRefreshToken,
  enableRefresh = false,
  exchangeConfig,
  firebaseApp,
  getPersistedRefreshToken,
  inactivityTimeoutMs,
  onRefreshTokenReceived,
  onSignOut,
  readImpersonationToken,
  sessionTimeoutMs = 12 * 60 * 60 * 1000,
  signInStrategies,
}: HetivoAuthProviderProps): React.JSX.Element {
  // Always call the latest onSignOut without making it a dependency of the Firebase listener
  // effect (callers usually pass an inline arrow, which would re-subscribe on every render).
  const onSignOutRef = useRef(onSignOut);
  onSignOutRef.current = onSignOut;
  const runOnSignOut = useCallback((): void => {
    try {
      onSignOutRef.current?.();
    } catch (error: unknown) {
      console.error('HetivoAuthProvider: onSignOut callback failed during sign-out.', error);
    }
  }, []);

  const attemptTokenRefreshRef = useRef<(() => Promise<boolean>) | null>(null);
  const exchangePausedRef = useRef(false);
  // True while a real (non-activation) Firebase session exists. onSignOut only has user data to
  // flush then: the activation flow signs in and out while paused, and the listener may deliver
  // that sign-out after resumeExchange(), so the paused flag alone cannot be trusted at that point.
  const hadSessionRef = useRef(false);
  // The uid whose cached data the query cache may still hold. Firebase can swap users (A -> B) without a
  // signed-out event in between, and the cache keys are not user-scoped, so B's session must not start over
  // A's data. Cleared only when A's data has been flushed, so it also survives an activation flow that
  // replaces the session: the next real sign-in as a different user then flushes.
  const lastUidRef = useRef<string | null>(null);
  const inactivityTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hetivoTokenRef = useRef<string | null>(null);
  const refreshPromiseRef = useRef<Promise<boolean> | null>(null);
  const refreshTokenRef = useRef<string | null>(null);
  const sessionTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Tracks the reason the session ended, so onAuthStateChanged can preserve it in state.
  const sessionEndedReasonRef = useRef<SessionEndedReason | null>(null);
  // Tracks whether a sign-out was initiated by a token exchange error, so that the
  // onAuthStateChanged callback does not overwrite the error state with null.
  const signOutDueToErrorRef = useRef(false);
  const tokenExpiryRef = useRef<number | null>(null);

  // A stable ref to the provider's setState, passed into useImpersonationSession so that
  // the impersonation expiry timer can update auth state. Populated after useState below.
  const setStateRef = useRef<React.Dispatch<React.SetStateAction<HetivoAuthState>>>(
    // Placeholder — always replaced before any effect fires (React renders synchronously)
    () => undefined,
  );

  const impersonation = useImpersonationSession(
    readImpersonationToken,
    { inactivityTimerRef, hetivoTokenRef, sessionTimerRef, tokenExpiryRef },
    setStateRef,
    runOnSignOut,
  );

  // Lazy initializer: when an impersonation token is provided the state is
  // immediately authenticated so TanStack Router's beforeLoad guard does not
  // redirect to /login on the first render.
  const [state, setState] = useState<HetivoAuthState>(() => impersonation.initialState);

  // Keep the setter ref up-to-date every render so useImpersonationSession's timer
  // callback always calls the current setState.
  setStateRef.current = setState;

  const auth = useMemo(() => getAuth(firebaseApp), [firebaseApp]);

  // Clear session timer on unmount
  useEffect(() => {
    return (): void => {
      if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
    };
  }, []);

  // Exchange Firebase token for Hetivo JWT
  const performTokenExchange = useCallback(
    async (firebaseUser: FirebaseUser): Promise<void> => {
      try {
        const idToken = await firebaseUser.getIdToken(/* forceRefresh */ true);
        const result = await exchangeFirebaseToken(idToken, exchangeConfig);

        hetivoTokenRef.current = result.token;
        refreshTokenRef.current = result.refreshToken ?? null;
        tokenExpiryRef.current = Date.now() + sessionTimeoutMs;

        // Persist the refresh token if a callback is provided
        if (result.refreshToken && onRefreshTokenReceived) {
          await onRefreshTokenReceived(result.refreshToken);
        }

        // Set session timeout timer
        if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
        sessionTimerRef.current = setTimeout(() => {
          sessionEndedReasonRef.current = SESSION_ENDED_REASON.MAX_DURATION;
          void firebaseSignOut(auth);
        }, sessionTimeoutMs);

        setState({
          error: null,
          isAuthenticated: true,
          isImpersonating: false,
          isLoading: false,
          hetivoToken: result.token,
          sessionEndedReason: null,
          user: {
            email: firebaseUser.email ?? '',
            name: firebaseUser.displayName ?? firebaseUser.email ?? '',
          },
        });
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Token exchange failed';
        hetivoTokenRef.current = null;
        refreshTokenRef.current = null;
        tokenExpiryRef.current = null;
        setState({
          error: message,
          isAuthenticated: false,
          isImpersonating: false,
          isLoading: false,
          hetivoToken: null,
          sessionEndedReason: null,
          user: null,
        });
        // Sign out of Firebase since we can't get a Hetivo token.
        // Mark the ref first so that onAuthStateChanged preserves the error state.
        signOutDueToErrorRef.current = true;
        await firebaseSignOut(auth);
      }
    },
    [auth, exchangeConfig, onRefreshTokenReceived, sessionTimeoutMs],
  );

  // Listen to Firebase auth state changes (skip when impersonating)
  useEffect(() => {
    if (impersonation.token) {
      // Impersonation mode: skip Firebase listener, mark loading as done
      return;
    }

    const unsubscribe = onAuthStateChanged(auth, (firebaseUser) => {
      if (firebaseUser) {
        if (exchangePausedRef.current) {
          // Activation flow — skip exchange, the hook will sign out shortly. Any earlier session is over
          // (the activation's sign-in replaced it), so its sign-out must not flush: that would refetch the
          // invitation and show "already accepted" instead of success. lastUidRef keeps the flush owed for
          // the next real sign-in as a different user.
          hadSessionRef.current = false;
          return;
        }
        // User switched with no signed-out event in between (A -> B): flush A's cached data before B's
        // token exchange so B never starts over it.
        if (lastUidRef.current !== null && firebaseUser.uid !== lastUidRef.current) {
          runOnSignOut();
        }
        lastUidRef.current = firebaseUser.uid;
        hadSessionRef.current = true;
        void performTokenExchange(firebaseUser);
      } else {
        // User signed out. Every path that ends a real Firebase session (explicit sign-out, idle timeout,
        // 12h timer, token-exchange failure, sign-out in another tab) lands here, so this is the one
        // place that guarantees the previous user's cached data is flushed. Skip it when no real session
        // existed: the initial no-user check on a cold load, and the activation flow's own sign-out
        // (flushing then would refetch the invitation and show "already accepted" instead of success).
        if (hadSessionRef.current) {
          hadSessionRef.current = false;
          lastUidRef.current = null;
          runOnSignOut();
        }
        hetivoTokenRef.current = null;
        refreshTokenRef.current = null;
        tokenExpiryRef.current = null;
        if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
        if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
        if (signOutDueToErrorRef.current) {
          // Error-initiated sign-out — preserve the error state already set by the catch block
          signOutDueToErrorRef.current = false;
        } else if (sessionEndedReasonRef.current) {
          // Timeout-initiated sign-out (inactivity or maxDuration) — surface the expiry reason
          const reason = sessionEndedReasonRef.current;
          sessionEndedReasonRef.current = null;
          setState({
            error: null,
            isAuthenticated: false,
            isImpersonating: false,
            isLoading: false,
            hetivoToken: null,
            sessionEndedReason: reason,
            user: null,
          });
        } else {
          // Normal sign-out — clear everything including error and expiry reason
          setState({
            error: null,
            isAuthenticated: false,
            isImpersonating: false,
            isLoading: false,
            hetivoToken: null,
            sessionEndedReason: null,
            user: null,
          });
        }
      }
    });
    return unsubscribe;
  }, [auth, impersonation.token, performTokenExchange, runOnSignOut]);

  const notifyActivity = useCallback((): void => {
    if (!inactivityTimeoutMs || !state.isAuthenticated) return;
    // Impersonation sessions have a hard expiry (JWT exp) — no inactivity timeout
    if (impersonation.token) return;
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    inactivityTimerRef.current = setTimeout(() => {
      sessionEndedReasonRef.current = SESSION_ENDED_REASON.INACTIVITY;
      void firebaseSignOut(auth);
    }, inactivityTimeoutMs);
  }, [auth, impersonation.token, inactivityTimeoutMs, state.isAuthenticated]);

  const pauseExchange = useCallback((): void => {
    exchangePausedRef.current = true;
  }, []);

  const resumeExchange = useCallback((): void => {
    exchangePausedRef.current = false;
  }, []);

  // Start the initial inactivity timer when the user becomes authenticated
  useEffect(() => {
    if (!inactivityTimeoutMs || !state.isAuthenticated) return;
    // Impersonation sessions have a hard expiry (JWT exp) — no inactivity timeout
    if (impersonation.token) return;

    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    inactivityTimerRef.current = setTimeout(() => {
      sessionEndedReasonRef.current = SESSION_ENDED_REASON.INACTIVITY;
      void firebaseSignOut(auth);
    }, inactivityTimeoutMs);

    return (): void => {
      if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);
    };
  }, [auth, impersonation.token, inactivityTimeoutMs, state.isAuthenticated]);

  const signInWithEmail = useMemo(() => {
    const emailStrategy = signInStrategies?.email;
    if (!emailStrategy) return undefined;
    return async (email: string, password: string): Promise<void> => {
      setState((prev) => ({ ...prev, error: null, isLoading: true, sessionEndedReason: null }));
      try {
        await emailStrategy(email, password);
        // onAuthStateChanged in the useEffect will handle token exchange
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Sign-in failed';
        setState((prev) => ({ ...prev, error: message, isLoading: false }));
        throw err;
      }
    };
  }, [signInStrategies]);

  const signInWithGoogle = useMemo(() => {
    const googleStrategy = signInStrategies?.google;
    if (!googleStrategy) return undefined;
    return async (): Promise<void> => {
      setState((prev) => ({ ...prev, error: null, isLoading: true, sessionEndedReason: null }));
      try {
        await googleStrategy();
        // onAuthStateChanged in the useEffect will handle token exchange
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Sign-in failed';
        setState((prev) => ({ ...prev, error: message, isLoading: false }));
        throw err;
      }
    };
  }, [signInStrategies]);

  const signInWithMicrosoft = useMemo(() => {
    const microsoftStrategy = signInStrategies?.microsoft;
    if (!microsoftStrategy) return undefined;
    return async (): Promise<void> => {
      setState((prev) => ({ ...prev, error: null, isLoading: true, sessionEndedReason: null }));
      try {
        await microsoftStrategy();
        // onAuthStateChanged in the useEffect will handle token exchange
      } catch (err: unknown) {
        const message = err instanceof Error ? err.message : 'Sign-in failed';
        setState((prev) => ({ ...prev, error: message, isLoading: false }));
        throw err;
      }
    };
  }, [signInStrategies]);

  const sendPasswordResetEmail = useMemo(() => {
    const resetPasswordStrategy = signInStrategies?.resetPassword;
    if (!resetPasswordStrategy) return undefined;
    return async (email: string): Promise<void> => {
      await resetPasswordStrategy(email);
    };
  }, [signInStrategies]);

  const signOut = useCallback(async (): Promise<void> => {
    hetivoTokenRef.current = null;
    refreshTokenRef.current = null;
    tokenExpiryRef.current = null;
    if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
    if (inactivityTimerRef.current) clearTimeout(inactivityTimerRef.current);

    // Preserve the logout reason before any auth-state transition so onAuthStateChanged
    // cannot overwrite the intended user-initiated sign-out message.
    sessionEndedReasonRef.current ??= SESSION_ENDED_REASON.USER_LOGOUT;

    // Clear the persisted refresh token from secure storage (mobile)
    if (clearPersistedRefreshToken) {
      try {
        await clearPersistedRefreshToken();
      } catch (error: unknown) {
        console.error('HetivoAuthProvider: clearPersistedRefreshToken failed during sign-out.', error);
      }
    }

    // Notify the consumer (e.g., to clear React Query cache) before Firebase sign-out
    // so all user-specific cached data is flushed before the next user can sign in.
    hadSessionRef.current = false;
    lastUidRef.current = null;
    runOnSignOut();

    if (!impersonation.token) {
      await firebaseSignOut(auth);
      return;
    }

    // Impersonation session: also sign out Firebase to prevent session restoration
    await firebaseSignOut(auth);
    impersonation.clearImpersonation();
    setState({
      error: null,
      isAuthenticated: false,
      isImpersonating: false,
      isLoading: false,
      hetivoToken: null,
      sessionEndedReason: SESSION_ENDED_REASON.USER_LOGOUT,
      user: null,
    });
  }, [auth, clearPersistedRefreshToken, impersonation, runOnSignOut]);

  /**
   * Reset the session timer after a successful token refresh or re-exchange.
   * Schedules the next refresh attempt (if enableRefresh) or a sign-out at the end of the session.
   */
  const resetSessionTimer = useCallback((): void => {
    if (sessionTimerRef.current) clearTimeout(sessionTimerRef.current);
    sessionTimerRef.current = setTimeout(() => {
      if (enableRefresh) {
        void attemptTokenRefreshRef.current?.();
      } else {
        sessionEndedReasonRef.current = SESSION_ENDED_REASON.MAX_DURATION;
        void firebaseSignOut(auth);
      }
    }, sessionTimeoutMs);
  }, [auth, enableRefresh, sessionTimeoutMs]);

  /**
   * Core refresh logic — calls POST /refresh, updates token refs, persists the new refresh token,
   * resets the session timer, and updates React state.
   * Returns true on success, false on any failure.
   */
  const executeTokenRefresh = useCallback(async (): Promise<boolean> => {
    try {
      const expiredToken = hetivoTokenRef.current;
      const storedRefreshToken = refreshTokenRef.current ?? (await getPersistedRefreshToken?.()) ?? null;

      if (!expiredToken || !storedRefreshToken) return false;

      const result = await refreshHetivoToken(expiredToken, storedRefreshToken, exchangeConfig);

      hetivoTokenRef.current = result.token;
      refreshTokenRef.current = result.refreshToken ?? null;
      tokenExpiryRef.current = Date.now() + sessionTimeoutMs;

      if (result.refreshToken && onRefreshTokenReceived) {
        await onRefreshTokenReceived(result.refreshToken);
      }

      resetSessionTimer();
      setState((prev) => ({ ...prev, hetivoToken: result.token }));
      return true;
    } catch {
      return false;
    }
  }, [exchangeConfig, getPersistedRefreshToken, onRefreshTokenReceived, resetSessionTimer, sessionTimeoutMs]);

  /**
   * Attempt to silently refresh the Hetivo JWT using the stored stateless refresh token.
   * Deduplicates concurrent calls: if a refresh is already in progress, returns the same promise.
   * Updates hetivoTokenRef, refreshTokenRef, tokenExpiryRef, and persists the new refresh token.
   * Returns true on success, false on failure.
   */
  const attemptTokenRefresh = useCallback((): Promise<boolean> => {
    if (refreshPromiseRef.current) return refreshPromiseRef.current;

    const promise = executeTokenRefresh().finally(() => {
      refreshPromiseRef.current = null;
    });

    refreshPromiseRef.current = promise;
    return promise;
  }, [executeTokenRefresh]);

  // Keep the ref in sync with the latest stable callback so resetSessionTimer can call it
  // without creating a circular useCallback dependency.
  attemptTokenRefreshRef.current = attemptTokenRefresh;

  /**
   * Fall back to Firebase re-exchange when refresh token is unavailable or exhausted.
   * Forces a new Firebase ID token and re-runs the full /exchange flow.
   * Returns true on success, false on failure.
   */
  const attemptFirebaseReExchange = useCallback(async (): Promise<boolean> => {
    try {
      const currentUser = auth.currentUser;
      if (!currentUser) return false;

      const idToken = await currentUser.getIdToken(/* forceRefresh */ true);
      const result = await exchangeFirebaseToken(idToken, exchangeConfig);

      hetivoTokenRef.current = result.token;
      refreshTokenRef.current = result.refreshToken ?? null;
      tokenExpiryRef.current = Date.now() + sessionTimeoutMs;

      if (result.refreshToken && onRefreshTokenReceived) {
        await onRefreshTokenReceived(result.refreshToken);
      }

      // Reset session timer for the new token
      resetSessionTimer();

      setState((prev) => ({ ...prev, hetivoToken: result.token }));
      return true;
    } catch {
      return false;
    }
  }, [auth, exchangeConfig, onRefreshTokenReceived, resetSessionTimer, sessionTimeoutMs]);

  const getToken = useCallback(async (): Promise<string | null> => {
    // Check if session has expired
    if (tokenExpiryRef.current && Date.now() >= tokenExpiryRef.current) {
      if (enableRefresh) {
        // Attempt silent refresh via stateless refresh token
        const refreshed = await attemptTokenRefresh();
        if (refreshed) return hetivoTokenRef.current;

        // Refresh failed — fall back to Firebase re-exchange
        const reExchanged = await attemptFirebaseReExchange();
        if (reExchanged) return hetivoTokenRef.current;
      }

      // No refresh enabled, or all attempts failed — sign out
      sessionEndedReasonRef.current = SESSION_ENDED_REASON.MAX_DURATION;
      await signOut();
      return null;
    }

    // Proactive refresh: if token expires within 5 minutes, refresh in background (fire and forget)
    if (enableRefresh && isTokenExpiringSoon(tokenExpiryRef.current)) {
      void attemptTokenRefresh();
    }

    return hetivoTokenRef.current;
  }, [attemptFirebaseReExchange, attemptTokenRefresh, enableRefresh, signOut]);

  const value = useMemo<HetivoAuthContextValue>(
    () => ({
      ...state,
      getToken,
      notifyActivity,
      pauseExchange,
      resumeExchange,
      sendPasswordResetEmail,
      signInWithEmail,
      signInWithGoogle,
      signInWithMicrosoft,
      signOut,
    }),
    [
      state,
      getToken,
      notifyActivity,
      pauseExchange,
      resumeExchange,
      sendPasswordResetEmail,
      signInWithEmail,
      signInWithGoogle,
      signInWithMicrosoft,
      signOut,
    ],
  );

  return <HetivoAuthContext.Provider value={value}>{children}</HetivoAuthContext.Provider>;
}

// --- Hook ---

export function useHetivoAuth(): HetivoAuthContextValue {
  const context = useContext(HetivoAuthContext);
  if (!context) {
    throw new Error('useHetivoAuth must be used within a <HetivoAuthProvider>');
  }
  return context;
}
